import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFarm } from '../contexts/FarmContext';
import { dashboardAPI, recordsAPI, farmAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { parseDateOnly, toLocalISO, startOfWeekMonday, addDays } from '../utils/dates';
import { formatPlace } from '../data/kenyaLocations';
import {
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ArrowRightIcon,
  MapPinIcon,
  TagIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  Line,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';

const LAYER_TYPES = new Set(['layer', 'dual']);
const TRAY_SIZE = 30;
const formatKes = (value) => `KES ${Number(value || 0).toLocaleString()}`;
const compactKes = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${n < 0 ? '-' : ''}${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${n < 0 ? '-' : ''}${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
  return String(Math.round(n));
};
const EggIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 3c-3.6 0-7 5.1-7 10.2C5 17.6 8.1 21 12 21s7-3.4 7-7.8C19 8.1 15.6 3 12 3z" />
  </svg>
);

const Dashboard = () => {
  const { farms, currentFarm } = useFarm();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [birds, setBirds] = useState(null);
  const [monthlyNet, setMonthlyNet] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [fallbackNetBirds, setFallbackNetBirds] = useState(null);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [eggStock, setEggStock] = useState(null);
  const [eggToday, setEggToday] = useState(null);
  const [showEggDetail, setShowEggDetail] = useState(false);
  const [eggLoading, setEggLoading] = useState(false);
  const [eggRange, setEggRange] = useState(() => {
    const end = parseDateOnly(new Date());
    const start = addDays(end, -29);
    return { start, end };
  });
  const [eggSeries, setEggSeries] = useState([]);
  const [eggSummary, setEggSummary] = useState(null);
  const [weeklyNetData, setWeeklyNetData] = useState([]);

  useEffect(() => {
    setSelectedCohortId('');
    setEggStock(null);
    setEggToday(null);
  }, [currentFarm?.id]);

  useEffect(() => {
    if (currentFarm) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [currentFarm, selectedCohortId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getOverview(currentFarm.id, { cohortId: selectedCohortId || undefined });
      setDashboardData(response.data);
      try { const b = await dashboardAPI.getBirds(currentFarm.id, { cohortId: selectedCohortId || undefined }); setBirds(b.data); } catch {}
      let list = [];
      try {
        const lc = await farmAPI.listCohorts(currentFarm.id);
        list = lc.data?.cohorts || [];
        setCohorts(list);
        const sumStart = list
          .filter((c) => !['completed', 'archived'].includes(c.status))
          .reduce((acc, c) => acc + (Number(c.startQty || c.startqty || 0)), 0);
        setFallbackNetBirds(sumStart);
      } catch (e) { console.warn('Load cohorts error', e?.message || e); }
      await loadMonthlyNetIncome();
      try {
        const selected = selectedCohortId ? list.find((c) => c.id === selectedCohortId) : null;
        const hasLayers = list.some((c) => LAYER_TYPES.has(c.type));
        const showEggs = selectedCohortId ? (selected && LAYER_TYPES.has(selected.type)) : hasLayers;
        if (showEggs) {
          const params = { ...(selectedCohortId ? { cohortId: selectedCohortId } : {}) };
          const es = await recordsAPI.getEggStock(currentFarm.id, params);
          setEggStock(es.data || null);
          const today = toLocalISO(new Date());
          const todayRes = await recordsAPI.getEggStock(currentFarm.id, { ...params, startDate: today, endDate: today });
          setEggToday(Number(todayRes.data?.collected || 0));
        } else {
          setEggStock(null);
          setEggToday(null);
        }
      } catch {
        setEggStock(null);
        setEggToday(null);
      }
      await loadWeeklyNetIncome();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyNetIncome = async () => {
    try {
      const now = parseDateOnly(new Date());
      const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const res = await recordsAPI.getRecords(currentFarm.id, {
        cohortId: selectedCohortId || undefined,
        startDate: toLocalISO(start),
        endDate: toLocalISO(end),
        limit: 999999
      });
      const items = res.data.records || [];

      const totals = new Map();
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        totals.set(key, {
          key,
          name: d.toLocaleString(undefined, { month: 'short' }),
          fullName: d.toLocaleString(undefined, { month: 'short', year: 'numeric' }),
          value: 0
        });
      }

      items.forEach((r) => {
        const d = parseDateOnly(r.date);
        if (!d) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!totals.has(key)) return;
        if (r.type === 'sales') {
          const amountPaid = Number(r.metadata?.amountPaid ?? (r.metadata?.paymentStatus === 'paid' ? r.amount : 0)) || 0;
          totals.get(key).value += amountPaid;
        } else if (r.type === 'expense') {
          totals.get(key).value -= Number(r.amount) || 0;
        }
      });

      setMonthlyNet(Array.from(totals.values()));
    } catch (e) {
      console.error('Load monthly net error:', e);
      setMonthlyNet(null);
    }
  };

  const loadWeeklyNetIncome = async () => {
    try {
      const today = parseDateOnly(new Date());
      const currentWeekStart = startOfWeekMonday(today);
      const windowStart = addDays(currentWeekStart, -11 * 7);

      const res = await recordsAPI.getRecords(currentFarm.id, {
        cohortId: selectedCohortId || undefined,
        startDate: toLocalISO(windowStart),
        endDate: toLocalISO(today),
        limit: 999999
      });
      const items = res.data.records || [];

      const weeks = [];
      for (let i = 0; i < 12; i++) {
        const weekStart = addDays(windowStart, i * 7);
        const weekEnd = addDays(weekStart, 6);
        weeks.push({
          start: weekStart,
          end: weekEnd,
          name: weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          weekly: 0,
          cumulative: 0
        });
      }

      items.forEach((r) => {
        const d = parseDateOnly(r.date);
        if (!d) return;
        const weekIdx = weeks.findIndex((w) => d >= w.start && d <= w.end);
        if (weekIdx === -1) return;
        if (r.type === 'sales') {
          const amountPaid = Number(r.metadata?.amountPaid ?? (r.metadata?.paymentStatus === 'paid' ? r.amount : 0)) || 0;
          weeks[weekIdx].weekly += amountPaid;
        } else if (r.type === 'expense') {
          weeks[weekIdx].weekly -= Number(r.amount) || 0;
        }
      });

      let cumulative = 0;
      setWeeklyNetData(weeks.map((w) => {
        cumulative += w.weekly;
        return { name: w.name, weekly: w.weekly, cumulative };
      }));
    } catch (e) {
      console.error('Load weekly net error:', e);
      setWeeklyNetData([]);
    }
  };

  const trendData = useMemo(() => {
    if (monthlyNet && monthlyNet.length) return monthlyNet;
    const now = parseDateOnly(new Date());
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        name: d.toLocaleString(undefined, { month: 'short' }),
        fullName: d.toLocaleString(undefined, { month: 'short', year: 'numeric' }),
        value: 0
      });
    }
    return months;
  }, [monthlyNet]);

  const currentMonthNet = useMemo(() => Number(trendData[trendData.length - 1]?.value || 0), [trendData]);
  const previousMonthNet = useMemo(() => Number(trendData[trendData.length - 2]?.value || 0), [trendData]);
  const thisWeekNet = useMemo(() => Number(weeklyNetData[weeklyNetData.length - 1]?.weekly || 0), [weeklyNetData]);
  const twelveWeekCumulative = useMemo(
    () => Number(weeklyNetData[weeklyNetData.length - 1]?.cumulative || 0),
    [weeklyNetData]
  );

  const loadEggDetail = async () => {
    if (!currentFarm) return;
    const selected = selectedCohortId ? cohorts.find((c) => c.id === selectedCohortId) : null;
    const hasLayers = cohorts.some((c) => LAYER_TYPES.has(c.type));
    const showEggs = selectedCohortId ? (selected && LAYER_TYPES.has(selected.type)) : hasLayers;
    if (!showEggs) return;
    try {
      setEggLoading(true);
      const params = { ...(selectedCohortId ? { cohortId: selectedCohortId } : {}) };
      const startISO = toLocalISO(eggRange.start);
      const endISO = toLocalISO(eggRange.end);
      const openRes = await recordsAPI.getEggStock(currentFarm.id, { ...params, startDate: startISO });
      const opening = Number(openRes.data?.openingBalance || 0);
      const recRes = await recordsAPI.getRecords(currentFarm.id, {
        ...params,
        startDate: startISO,
        endDate: endISO,
        limit: 999999
      });
      const items = recRes.data?.records || [];
      const knownFlocks = new Set((cohorts || []).map((c) => c.id));
      const byDay = new Map();
      const start = parseDateOnly(eggRange.start);
      const end = parseDateOnly(eggRange.end);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const k = toLocalISO(d);
        byDay.set(k, { date: k, collected: 0, sold: 0, adjustments: 0, closing: 0 });
      }
      items.forEach((r) => {
        if (r.cohortId && !knownFlocks.has(r.cohortId)) return;
        const k = toLocalISO(r.date);
        if (!byDay.has(k)) return;
        if (r.type === 'egg_collection') byDay.get(k).collected += Number(r.quantity || 0);
        else if (r.type === 'sales' && ['eggs', 'egg'].includes(String(r.category || '').toLowerCase())) byDay.get(k).sold += Number(r.quantity || 0);
        else if (r.type === 'stock' && String(r.category || '').toLowerCase() === 'eggs') byDay.get(k).adjustments += Number(r.quantity || 0);
      });
      let running = opening;
      const series = Array.from(byDay.values()).map((row) => {
        running = running + row.collected - row.sold + row.adjustments;
        return {
          name: parseDateOnly(row.date).toLocaleDateString(undefined, { month: 'short', day: '2-digit' }),
          ...row,
          closing: running
        };
      });
      const sums = series.reduce((acc, r) => ({
        collected: acc.collected + r.collected,
        sold: acc.sold + r.sold,
        adjustments: acc.adjustments + r.adjustments
      }), { collected: 0, sold: 0, adjustments: 0 });
      setEggSeries(series);
      setEggSummary({ opening, ...sums, closing: running });
    } catch (e) {
      console.error('Load egg detail error', e);
      setEggSeries([]);
      setEggSummary(null);
    } finally {
      setEggLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!farms || farms.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl shadow-xl p-8 text-center text-white">
          <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-6">
            <BuildingOfficeIcon className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Create Your First Farm</h2>
          <p className="text-green-100 mb-8 max-w-md mx-auto">
            Get started with Omabra by setting up your poultry farm. Track birds, manage activities, and grow your business.
          </p>
          <Link
            to="/farm/setup"
            className="inline-flex items-center px-6 py-3 bg-white text-green-700 rounded-lg hover:bg-green-50 transition-colors font-semibold"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Farm
          </Link>
        </div>
      </div>
    );
  }

  if (!currentFarm) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <BuildingOfficeIcon className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900">No active farm</h3>
        <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
          {farms?.length
            ? 'Your farms are disabled. Enable one from the Farms page to use the dashboard.'
            : 'Choose a farm from the sidebar to view its dashboard.'}
        </p>
        <Link to="/farms" className="inline-flex items-center mt-4 text-sm font-medium text-green-700">
          Go to Farms
        </Link>
      </div>
    );
  }

  const { overview } = dashboardData || {};

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-white/5 rounded-full"></div>
        
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPinIcon className="h-4 w-4 text-green-200" />
              <span className="text-green-200 text-sm">{formatPlace(currentFarm) || 'Location not set'}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold">{currentFarm.name}</h1>
            <p className="text-green-100 mt-1">Welcome back! Here's your farm overview</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/activities"
              className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
            >
              <CalendarDaysIcon className="h-4 w-4 mr-2" />
              Activities
            </Link>
            <Link
              to="/records"
              className="inline-flex items-center px-4 py-2 bg-white text-green-700 rounded-lg hover:bg-green-50 transition-colors text-sm font-medium"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Record
            </Link>
          </div>
        </div>
      </div>

      {/* Flock selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Dashboard Overview</h2>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Flock:</label>
          <select 
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500" 
            value={selectedCohortId} 
            onChange={(e)=>setSelectedCohortId(e.target.value)}
          >
            <option value="">All flocks</option>
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}{c.name?.toLowerCase().includes('primary') ? ' (Primary)' : ''}</option>)}
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg shadow-blue-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Birds</p>
              <p className="text-3xl font-bold mt-1">
                {birds?.netBirds ?? (fallbackNetBirds ?? currentFarm.setupData?.chickenCount ?? 0)}
              </p>
              <p className="text-blue-200 text-xs mt-2">Net count</p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <BuildingOfficeIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white shadow-lg shadow-amber-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-100 text-sm">Pending Tasks</p>
              <p className="text-3xl font-bold mt-1">
                {overview?.activityStats?.find(s => s.status === 'pending')?.count || 0}
              </p>
              <p className="text-amber-200 text-xs mt-2">Activities</p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-rose-500 to-red-600 rounded-xl p-5 text-white shadow-lg shadow-rose-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-rose-100 text-sm">Overdue</p>
              <p className="text-3xl font-bold mt-1">
                {overview?.activityStats?.find(s => s.status === 'overdue')?.count || 0}
              </p>
              <p className="text-rose-200 text-xs mt-2">Need attention</p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className={`rounded-xl p-5 text-white shadow-lg ${currentMonthNet >= 0 ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-500/20' : 'bg-gradient-to-br from-rose-500 to-red-600 shadow-rose-500/20'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-sm">{new Date().toLocaleString(undefined, { month: 'short' })} net</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1">
                {formatKes(currentMonthNet)}
              </p>
              <p className="text-white/80 text-xs mt-2">
                This week {formatKes(thisWeekNet)}
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <ChartBarIcon className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Egg Stock Card (Conditional) */}
      {(() => {
        const selected = selectedCohortId ? cohorts.find((c) => c.id === selectedCohortId) : null;
        const hasLayers = cohorts.some((c) => LAYER_TYPES.has(c.type));
        const showEgg = selectedCohortId ? (selected && LAYER_TYPES.has(selected.type)) : hasLayers;
        if (!showEgg) return null;
        const closing = eggStock?.closingBalance ?? null;
        const trays = closing != null ? Math.floor(Number(closing) / TRAY_SIZE) : null;
        const rem = closing != null ? Number(closing) % TRAY_SIZE : null;
        return (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <EggIcon className="h-8 w-8 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {selectedCohortId ? 'Egg stock' : 'Egg stock · all laying flocks'}
                  </p>
                  <p className="text-3xl font-bold text-amber-900">{closing != null ? Number(closing).toLocaleString() : '—'}</p>
                  {closing != null && (
                    <p className="text-sm text-amber-700">
                      {trays} trays of {TRAY_SIZE} + {rem} eggs
                      {eggToday != null && (
                        <span className="ml-2 text-amber-800">· Today {eggToday >= 0 ? '+' : ''}{Number(eggToday).toLocaleString()}</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 self-start">
                <Link
                  to="/records?adjustEggs=1"
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                >
                  Adjust stock
                </Link>
                <button
                  className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors text-sm font-medium"
                  onClick={() => { setShowEggDetail(true); loadEggDetail(); }}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link to="/records" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-green-200 transition-all group">
          <div className="p-2 bg-green-50 rounded-lg w-fit group-hover:bg-green-100 transition-colors">
            <ClipboardDocumentListIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Records</p>
          <p className="text-xs text-gray-500">Track data</p>
        </Link>
        <Link to="/shop" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-amber-200 transition-all group">
          <div className="p-2 bg-amber-50 rounded-lg w-fit group-hover:bg-amber-100 transition-colors">
            <ShoppingCartIcon className="h-5 w-5 text-amber-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Shop</p>
          <p className="text-xs text-gray-500">Sell products</p>
        </Link>
        <Link to="/market" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all group">
          <div className="p-2 bg-blue-50 rounded-lg w-fit group-hover:bg-blue-100 transition-colors">
            <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Marketplace</p>
          <p className="text-xs text-gray-500">Buy supplies</p>
        </Link>
        <Link to="/ai-assistant" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-purple-200 transition-all group">
          <div className="p-2 bg-purple-50 rounded-lg w-fit group-hover:bg-purple-100 transition-colors">
            <SparklesIcon className="h-5 w-5 text-purple-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">AI Assistant</p>
          <p className="text-xs text-gray-500">Get help</p>
        </Link>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Net Income */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Monthly net income</h2>
              <p className="text-sm text-gray-500">Paid sales minus expenses, last 12 months</p>
            </div>
            <div className="text-right">
              <p className={`text-sm font-semibold ${currentMonthNet >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {formatKes(currentMonthNet)}
              </p>
              <p className="text-xs text-gray-500">
                Last month {formatKes(previousMonthNet)}
              </p>
            </div>
          </div>
          <div className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                    tickFormatter={compactKes}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    formatter={(value) => [formatKes(value), 'Monthly net']}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {trendData.map((entry, index) => (
                      <Cell key={entry.key || index} fill={Number(entry.value) >= 0 ? '#10b981' : '#f43f5e'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Upcoming Activities */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Upcoming Activities</h2>
              <p className="text-sm text-gray-500">Tasks that need attention</p>
            </div>
            <Link to="/activities" className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center">
              View All
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
          
          {overview?.upcomingActivities?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {overview.upcomingActivities.slice(0, 5).map((activity) => (
                <div key={activity.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        activity.priority === 'critical' ? 'bg-red-500' :
                        activity.priority === 'high' ? 'bg-amber-500' :
                        activity.priority === 'medium' ? 'bg-blue-500' : 'bg-gray-400'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(activity.scheduledDate).toLocaleDateString()}
                          {activity.cohort && ` • ${activity.cohort.name}`}
                          {Array.isArray(activity.metadata?.checklist)
                            && activity.metadata.checklist.length > 0
                            && new Date(activity.scheduledDate).getTime() <= Date.now()
                            ? ` • ${activity.metadata.checklist.filter((step) => step.done).length}/${activity.metadata.checklist.length} steps`
                            : ''}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      activity.priority === 'critical' ? 'bg-red-100 text-red-700' :
                      activity.priority === 'high' ? 'bg-amber-100 text-amber-700' :
                      activity.priority === 'medium' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {activity.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <CalendarDaysIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No upcoming activities</p>
              <p className="text-sm mt-1">All caught up!</p>
            </div>
          )}
        </div>
      </div>

      {/* Weekly Cumulative Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Weekly net income</h2>
            <p className="text-sm text-gray-500">Last 12 weeks, Monday to Sunday. Bars are that week; the line is the running total.</p>
          </div>
          <div className="grid grid-cols-2 gap-4 text-right">
            <div>
              <p className="text-xs text-gray-500">This week</p>
              <p className={`text-sm font-semibold ${thisWeekNet >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatKes(thisWeekNet)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">12-week total</p>
              <p className={`text-sm font-semibold ${twelveWeekCumulative >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>{formatKes(twelveWeekCumulative)}</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="h-72">
            {weeklyNetData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={weeklyNetData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={compactKes}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    formatter={(value, name) => [formatKes(value), name === 'weekly' ? 'This week' : 'Running total']}
                  />
                  <Legend formatter={(value) => (value === 'weekly' ? 'Weekly net' : 'Cumulative')} />
                  <Bar dataKey="weekly" name="weekly" radius={[4, 4, 0, 0]}>
                    {weeklyNetData.map((entry, index) => (
                      <Cell key={`week-${index}`} fill={Number(entry.weekly) >= 0 ? '#34d399' : '#fb7185'} />
                    ))}
                  </Bar>
                  <Line type="monotone" dataKey="cumulative" name="cumulative" stroke="#2563eb" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p>No income data available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Records & Farm Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Records */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Recent Records</h2>
              <p className="text-sm text-gray-500">Latest farm entries</p>
            </div>
            <Link to="/records" className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center">
              View All
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
          
          {overview?.recentRecords?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {overview.recentRecords.slice(0, 5).map((record) => (
                <div key={record.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900 capitalize">
                        {record.type.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(record.date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      {record.quantity && (
                        <p className="text-sm font-semibold text-gray-900">
                          {record.quantity} {record.unit}
                        </p>
                      )}
                      {record.amount && (
                        <p className="text-sm font-semibold text-gray-900">
                          KES {Number(record.amount).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No recent records</p>
              <p className="text-sm mt-1">Start adding records to track your farm</p>
            </div>
          )}
        </div>

        {/* Farm Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Farm Information</h2>
            <p className="text-sm text-gray-500">Current flock details</p>
          </div>
          <div className="p-6">
            <div className="grid gap-4">
              <InfoItem icon={MapPinIcon} label="Location" value={formatPlace(currentFarm) || 'Not specified'}/>
              {selectedCohortId ? (
                <>
                  {(() => { const c = cohorts.find(x => x.id === selectedCohortId); return <InfoItem icon={BuildingOfficeIcon} label="Flock Type" value={(c?.type || '—')} valueClass="capitalize"/>; })()}
                  {(() => { 
                    const c = cohorts.find(x => x.id === selectedCohortId); 
                    if (!c) return null; 
                    const sd = c.startDate || c.start_date; 
                    const base = Number(c.initialAgeDays || 0); 
                    const age = sd ? base + Math.max(0, Math.floor((Date.now() - new Date(sd).getTime())/(1000*60*60*24))) : base; 
                    return <InfoItem icon={CalendarDaysIcon} label="Age" value={`${age} days`}/>; 
                  })()}
                  <InfoItem icon={BuildingOfficeIcon} label="Net Birds" value={birds?.netBirds ?? '—'}/>
                  {(() => { const c = cohorts.find(x => x.id === selectedCohortId); return <InfoItem icon={TagIcon} label="Breed" value={(c?.breed || '—')}/>; })()}
                </>
              ) : (
                <p className="text-sm text-gray-500 italic">Select a specific flock to see details</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-gray-500" />
              Need Help?
            </h3>
            <p className="text-sm text-gray-600 mt-1">Our support team is here to assist you</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://wa.me/254743673899"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <PhoneIcon className="h-4 w-4 mr-2" />
              WhatsApp
            </a>
            <a
              href="mailto:omabraservices@gmail.com"
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <EnvelopeIcon className="h-4 w-4 mr-2" />
              Email Support
            </a>
          </div>
        </div>
      </div>

      {/* Egg Detail Modal */}
      {showEggDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Egg Stock Details</h3>
              <button 
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={()=>setShowEggDetail(false)}
              >
                <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <label className="text-sm text-gray-600">From</label>
                <input 
                  type="date" 
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm" 
                  value={toLocalISO(eggRange.start)}
                  onChange={(e) => setEggRange((r) => ({ ...r, start: parseDateOnly(e.target.value) || r.start }))} 
                />
                <label className="text-sm text-gray-600">to</label>
                <input 
                  type="date" 
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm" 
                  value={toLocalISO(eggRange.end)}
                  onChange={(e) => setEggRange((r) => ({ ...r, end: parseDateOnly(e.target.value) || r.end }))} 
                />
                <button 
                  className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50" 
                  onClick={loadEggDetail} 
                  disabled={eggLoading}
                >
                  {eggLoading ? 'Loading…' : 'Apply'}
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Opening</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">{eggSummary?.opening ?? '—'}</div>
                </div>
                <div className="p-4 rounded-xl bg-green-50 border border-green-100">
                  <div className="text-xs text-green-600 uppercase tracking-wide">Collected</div>
                  <div className="text-2xl font-bold text-green-700 mt-1">{eggSummary?.collected ?? '—'}</div>
                </div>
                <div className="p-4 rounded-xl bg-rose-50 border border-rose-100">
                  <div className="text-xs text-rose-600 uppercase tracking-wide">Sold</div>
                  <div className="text-2xl font-bold text-rose-700 mt-1">{eggSummary?.sold ?? '—'}</div>
                </div>
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <div className="text-xs text-blue-600 uppercase tracking-wide">Adjustments</div>
                  <div className="text-2xl font-bold text-blue-700 mt-1">{eggSummary?.adjustments ?? '—'}</div>
                </div>
              </div>

              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={eggSeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorEgg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      formatter={(value) => [value, 'Closing Stock']}
                    />
                    <Area type="monotone" dataKey="closing" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorEgg)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="flex items-center justify-between">
                  <span className="text-amber-800 font-medium">Closing Balance</span>
                  <span className="text-2xl font-bold text-amber-900">{eggSummary?.closing ?? '—'} eggs</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

const InfoItem = ({ icon: Icon, label, value, valueClass }) => (
  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
    <div className="p-2 bg-white rounded-lg border border-gray-100">
      <Icon className="h-5 w-5 text-gray-500" />
    </div>
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-sm font-medium text-gray-900 ${valueClass || ''}`}>{value}</p>
    </div>
  </div>
);
